import { useEffect, useState } from "react";
import { api, ApiError } from "../api";
import { BasicControl, FieldRow } from "../components/FormField";
import { applyResolvedOptions, compileCommand, initialCommandValues, missingRequired, type CommandValue } from "../commandArgs";
import type { CommandDeclaration, CommandField, ResourceSummary } from "../types";

/**
 * Profile commands, offered in the composer's + menu. Each one sends exactly the text typing
 * `/name args` would send -- see docs/adr/0001-commands-compile-to-message-text.md -- so there is
 * one dispatch path and the two can never drift.
 *
 * A command with no fields and no confirmation sends on the first press. Anything else opens a
 * dialog first.
 */
export function useCommands({
	workflows,
	sessionId,
	streaming,
	onSend,
	onError,
}: {
	workflows: ResourceSummary[];
	sessionId: string;
	/** A turn is in flight. Ordinary commands still queue; confirming ones refuse to. */
	streaming: boolean;
	/** Returns null on success, or the message to show on failure -- the caller decides where it goes. */
	onSend: (text: string) => Promise<string | null>;
	onError: (message: string) => void;
}) {
	const [open, setOpen] = useState<CommandDeclaration | null>(null);
	// The no-dialog path needs its own in-flight guard, or a double press queues the command twice.
	const [sending, setSending] = useState<string | null>(null);

	/** Whether pressing it opens the dialog. A script command always does: its output is shown there. */
	const opensDialog = (cmd: CommandDeclaration) => !(cmd.workflow && cmd.fields.length === 0 && !cmd.confirm);

	/** A command that spends money is not queued behind a turn in flight; everything else queues like a typed message. */
	const disabled = (cmd: CommandDeclaration) => (streaming && Boolean(cmd.confirm)) || sending !== null;

	async function press(cmd: CommandDeclaration) {
		if (!opensDialog(cmd)) {
			const out = compileCommand(cmd, {});
			if (!out.ok) {
				onError(out.error);
				return;
			}
			setSending(cmd.name);
			try {
				const failure = await onSend(out.text);
				if (failure) onError(failure);
			} finally {
				setSending(null);
			}
			return;
		}
		setOpen(cmd);
	}

	const dialog = open && (
		<CommandDialog
			// A key makes the remount, and the fresh state, true by construction.
			key={open.name}
			command={open}
			hint={open.workflow ? workflows.find((w) => w.name === open.workflow)?.argumentHint : undefined}
			sessionId={sessionId}
			streaming={streaming}
			onClose={() => setOpen(null)}
			// Close only on success, so a failed send keeps what was typed.
			onSend={async (text) => {
				const failure = await onSend(text);
				if (!failure) setOpen(null);
				return failure;
			}}
		/>
	);

	return { press, opensDialog, disabled, sending, dialog };
}

function CommandDialog({
	command,
	hint,
	sessionId,
	streaming,
	onClose,
	onSend,
}: {
	command: CommandDeclaration;
	hint?: string;
	sessionId: string;
	/** A turn is in flight. Re-checked here because the dialog can already be open when one starts. */
	streaming: boolean;
	onClose: () => void;
	/** Returns null on success, or the message to show on failure -- shown in this dialog's own error slot. */
	onSend: (text: string) => Promise<string | null>;
}) {
	const [fields, setFields] = useState(command.fields);
	const [values, setValues] = useState<Record<string, CommandValue>>(() => initialCommandValues(command.fields));
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [loadingOptions, setLoadingOptions] = useState(() => command.fields.some((f) => f.options_from));
	// A script command's output once it succeeded; the dialog then only offers to close.
	const [output, setOutput] = useState<string | null>(null);

	// Selects with `options_from` start on their static options and switch once the workspace answers.
	// A failed lookup keeps the static ones.
	useEffect(() => {
		if (!command.fields.some((f) => f.options_from)) return;
		let live = true;
		api
			.commandOptions(sessionId, command.name)
			.then((res) => {
				if (!live) return;
				setFields(applyResolvedOptions(command.fields, {}, res.options).fields);
				setValues((prev) => applyResolvedOptions(command.fields, prev, res.options).values);
			})
			.catch(() => {})
			.finally(() => {
				if (live) setLoadingOptions(false);
			});
		return () => {
			live = false;
		};
	}, [command, sessionId]);

	function setValue(name: string, value: CommandValue) {
		setValues((prev) => ({ ...prev, [name]: value }));
		setError(null);
	}

	async function submit() {
		// The bar refuses to OPEN a confirming command mid-turn, but the dialog can already be open
		// when a turn starts -- another client on a shared session, or a schedule. Re-check here:
		// this is the guard on the command that spends money, and a guard that only holds when
		// nothing else is touching the session is not a guard.
		if (streaming && command.confirm) {
			setError("当前回合未结束，请稍后执行");
			return;
		}
		const missing = missingRequired(fields, values);
		if (missing.length > 0) {
			setError(`必填项未填：${missing.map((f) => f.label).join("、")}`);
			return;
		}
		if (command.script) {
			setBusy(true);
			try {
				const res = await api.runCommand(sessionId, command.name, values);
				setOutput(res.output || "已完成");
			} catch (err) {
				setError(err instanceof ApiError ? err.message : "运行失败");
			} finally {
				setBusy(false);
			}
			return;
		}
		const out = compileCommand({ ...command, fields }, values);
		if (!out.ok) {
			setError(out.error);
			return;
		}
		setBusy(true);
		try {
			const failure = await onSend(out.text);
			if (failure) setError(failure);
		} finally {
			setBusy(false);
		}
	}

	// Reuses the skill manager's modal structure exactly (SkillManager.tsx:141-147): `.modal` is
	// the fixed backdrop, `.modal-card` the panel, and the header is a bare <header> styled by
	// `.modal-card > header`. Only the card's own sizing differs, via `command-card`.
	return (
		<div className="modal" role="dialog" aria-label={command.label} onClick={onClose}>
			<div className="modal-card command-card" onClick={(e) => e.stopPropagation()}>
				<header>
					<span>
						<b>{command.label}</b> <code>{command.workflow ? `/${command.workflow}` : command.script}</code>
					</span>
					<button className="ghost" onClick={onClose}>
						×
					</button>
				</header>

				<div className="command-body">
					{command.confirm && <p className="banner warn">{command.confirm}</p>}
					{hint && command.fields.length === 0 && <p className="hint">{hint}</p>}

					<div className="form-fields">
						{fields.map((f, i) => (
							<CommandField
								key={f.name}
								field={f}
								// The argument-hint describes the whole argument string, so it belongs on
								// the first field rather than being repeated under every one.
								hint={i === 0 ? hint : undefined}
								// Non-null: `values` is seeded by `initialCommandValues` for every field in
								// `command.fields` and only ever updated by key, so `f.name` is always present.
								// A `??` fallback here is what silently re-introduced "" for a select before.
								value={values[f.name]!}
								sessionId={sessionId}
								disabled={busy || output !== null}
								onChange={(v) => setValue(f.name, v)}
								// The dialog's own error slot: the hook's `onError` lands in Chat's
								// error list behind the modal backdrop, invisible while the dialog is open.
								onError={setError}
							/>
						))}
					</div>

					{error && <p className="banner error">{error}</p>}
					{output !== null && <p className="banner command-output">{output}</p>}
				</div>

				<footer className="command-foot">
					{output !== null ? (
						<button onClick={onClose}>关闭</button>
					) : (
						<>
							<button className="ghost" onClick={onClose} disabled={busy}>
								取消
							</button>
							<button className="primary" onClick={() => void submit()} disabled={busy || loadingOptions || (streaming && Boolean(command.confirm))}>
								{busy ? (command.script ? "执行中…" : "发送中…") : command.confirm ? "确认执行" : command.script ? "执行" : "发送"}
							</button>
						</>
					)}
				</footer>
			</div>
		</div>
	);
}

/** A file field uploads immediately and holds the workspace-relative path as its value. */
function CommandField({
	field,
	hint,
	value,
	sessionId,
	disabled,
	onChange,
	onError,
}: {
	field: CommandField;
	hint?: string;
	value: CommandValue;
	sessionId: string;
	disabled: boolean;
	onChange: (value: CommandValue) => void;
	onError: (message: string) => void;
}) {
	const [uploading, setUploading] = useState(false);
	const label = field.required ? `${field.label} *` : field.label;

	if (field.type !== "file") {
		// `field.type` narrows here, but `field` itself keeps its declared `CommandFieldType` (six
		// options) as far as the type checker is concerned when passed whole -- so rebuild the
		// object with the already-narrowed `type` to satisfy `BasicControl`'s five-type `FieldSpec`.
		const basicField = { ...field, type: field.type };
		return (
			<FieldRow label={label} hint={hint}>
				<BasicControl field={basicField} value={value} disabled={disabled} onChange={onChange} />
			</FieldRow>
		);
	}

	async function upload(files: FileList | null) {
		if (!files?.length) return;
		setUploading(true);
		try {
			const res = await api.upload(sessionId, [...files]);
			// The PATH is the argument, not the file's content: the agent reads it with its fs
			// tools, and a path cannot be reflowed the way pasted prose is.
			if (res.files[0]) onChange(res.files[0].relPath);
		} catch (err) {
			onError(err instanceof ApiError ? err.message : "上传失败");
		} finally {
			setUploading(false);
		}
	}

	return (
		<FieldRow label={label} hint={hint}>
			<span className="command-file">
				<input type="file" disabled={disabled || uploading} onChange={(e) => void upload(e.target.files)} />
				{uploading ? <span className="hint">上传中…</span> : typeof value === "string" && value ? <code>{value}</code> : null}
			</span>
		</FieldRow>
	);
}
